import { Suspense, type ReactNode } from 'react';
import { PermissionRoute } from './PermissionRoute';

/**
 * One Suspense boundary around a lazily-loaded route element, and the permission gate for it.
 *
 * Deliberately a boundary per route element rather than one around the whole outlet: the
 * fallback is `null`, so a route that loads instantly from cache never flickers, and one that
 * does not leaves the chrome in place while only the panel is empty. A single boundary higher
 * up would blank the sidebar and navbar too.
 *
 * **The permission gate rides here for the same reason** — the shell stays, and only the panel
 * says the session may not open this screen. It cannot live in `VerticalLayout`, which is
 * `shared/app-shell` and may not import this app's nav config, and it should not wrap
 * `AppLayout`, which would take the sidebar away along with the screen. Every route element in
 * the table goes through this component, so putting it here is what makes it impossible to
 * register a screen and forget to gate it. A route with no nav owner is unrestricted, which is
 * what `/profile` and `/dashboard` need — see `route-permissions.ts`.
 *
 * It lives in its own file so `routes.tsx` exports only the route table — a module that mixes
 * a component with other exports breaks Fast Refresh for the component.
 */
export const LazyRoute = ({ children }: { children: ReactNode }): ReactNode => (
  <Suspense fallback={null}>
    <PermissionRoute>{children}</PermissionRoute>
  </Suspense>
);
